import { type ComponentProps, type FC, memo } from "react";
import { Button, ButtonKind, ButtonLink, ButtonVariant } from "@stubhub/broadway-web";
import { NotificationIcon } from "@stubhub/broadway-web/icons/functional";
import { useContent } from "src/shared/components/content";
import { soldOutWrapperStyle } from "./ResidencyShowtimeButton.styles";

type ButtonProps = ComponentProps<typeof Button>;

export interface ResidencyShowtimeButtonProps {
  "aria-label"?: string;
  classes?: ButtonProps["classes"];
  /** Renders a ButtonLink for available events — mutually exclusive with onClick */
  href?: string;
  /** Renders a disabled sold-out button with notification bell icon */
  isSoldOut: boolean;
  /** Renders a disabled "TBA" button; time/price are ignored */
  isTba: boolean;
  kind?: ButtonProps["kind"];
  /** Click handler for available events — mutually exclusive with href */
  onClick?: () => void;
  /** Called when the sold-out button wrapper is clicked */
  onSoldOutClick?: () => void;
  /** Price appended to the label for available events (e.g. "$45") */
  price?: string | null;
  /** Show notification bell icon on sold-out button (default: true) */
  showNotificationIcon?: boolean;
  /** Append "· Sold Out" to the label in sold-out state */
  showSoldOutText?: boolean;
  /** Pre-formatted time string (e.g. "7:00 PM") */
  size?: ButtonProps["size"];
  time: string;
  variant?: ButtonProps["variant"];
}

export const ResidencyShowtimeButton: FC<ResidencyShowtimeButtonProps> = memo(
  ({
    "aria-label": ariaLabel,
    classes,
    href,
    isSoldOut,
    isTba,
    kind = ButtonKind.Primary,
    onClick,
    price,
    showNotificationIcon = false,
    showSoldOutText = false,
    onSoldOutClick,
    size,
    time,
    variant = ButtonVariant.Outline,
  }) => {
    const tbaLabel = useContent("cid$#4099720#"); // TBA
    const soldOutLabel = useContent("cid$#4099728#"); // Sold Out
    const sizeProps = size ? { size } : {};
    const classesProps = classes ? { classes } : {};
    const ariaProps = ariaLabel ? { "aria-label": ariaLabel } : {};

    if (isTba) {
      return (
        <Button type="button" kind={kind} variant={variant} {...sizeProps} {...classesProps} {...ariaProps} disabled>
          {time || tbaLabel}
        </Button>
      );
    }

    if (isSoldOut) {
      const label = showSoldOutText ? `${time} · ${soldOutLabel}` : time;
      return (
        <div className={soldOutWrapperStyle} role="presentation" onClick={onSoldOutClick}>
          <Button
            disabled
            kind={kind}
            type="button"
            variant={variant}
            {...ariaProps}
            {...classesProps}
            {...sizeProps}
            {...(showNotificationIcon ? { endIcon: NotificationIcon } : {})}
          >
            {label}
          </Button>
        </div>
      );
    }

    const label = price ? `${time} · ${price}` : time;

    if (!!href) {
      return (
        <ButtonLink
          href={href}
          kind={kind}
          rel="noopener noreferrer"
          target="_blank"
          variant={variant}
          {...ariaProps}
          {...classesProps}
          {...sizeProps}
        >
          {label}
        </ButtonLink>
      );
    }

    const onClickProps = onClick === undefined ? {} : { onClick };

    return (
      <Button
        kind={kind}
        type="button"
        variant={variant}
        {...ariaProps}
        {...classesProps}
        {...onClickProps}
        {...sizeProps}
      >
        {label}
      </Button>
    );
  },
);

ResidencyShowtimeButton.displayName = "ResidencyShowtimeButton";
